<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Second-step modal that appears once a user clicks "Verify" inside
 * any of the verify-flavoured gates (availability / offer / dev-contact).
 *
 *  • 6 numeric cells with auto-advance, backspace step-back, paste support.
 *  • 30-second resend countdown.
 *  • Sends the OTP on mount via POST /api/auth/phone/send.
 *  • Submits to POST /api/auth/phone/verify on full code or button click.
 *  • Emits `@verified` on success — parent calls
 *    `useAccessGate().resumeAfterVerify()` to run the queued action.
 */

const { t } = useI18n();
const { toErrorMessage } = useErrorMessage();

const { userPhoneMasked = '+971 50 ••• ••24' } = defineProps<{
  userPhoneMasked?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'verified'): void;
  (e: 'change-number'): void;
}>();

const titleId = useId();

const code = ref<string[]>(['', '', '', '', '', '']);
const inputs = ref<HTMLInputElement[]>([]);
const setInputRef = (el: Element | null, idx: number) => {
  if (el instanceof HTMLInputElement) inputs.value[idx] = el;
};

const errorMessage = ref<string | null>(null);
const isBlocked = ref(false);
const resendTimer = ref(30);
let resendInterval: ReturnType<typeof setInterval> | null = null;

const { mutateAsync: sendCode, isPending: isSending } = useSendPhoneOtp();
const { mutateAsync: verifyCode, isPending: isVerifying } = useVerifyPhoneOtp();

const codeString = computed(() => code.value.join(''));
const isComplete = computed(() => codeString.value.length === 6);

const startResendTimer = () => {
  if (resendInterval) clearInterval(resendInterval);
  resendTimer.value = 30;
  resendInterval = setInterval(() => {
    resendTimer.value -= 1;
    if (resendTimer.value <= 0 && resendInterval) {
      clearInterval(resendInterval);
      resendInterval = null;
    }
  }, 1000);
};

const handleSend = async () => {
  errorMessage.value = null;
  try {
    await sendCode();
    startResendTimer();
  } catch (err: unknown) {
    if (isEmailFallbackOffered(err)) {
      isBlocked.value = true;
      return;
    }
    // Never show the raw FetchError text ("[POST] /api/…: 400") — coded message or the translated generic one.
    errorMessage.value = toErrorMessage(err, t('property.access.otpEntryModal.errorSendFailed'));
  }
};

const handleSubmit = async () => {
  if (!isComplete.value || isVerifying.value) return;
  errorMessage.value = null;
  try {
    await verifyCode(codeString.value);
    emit('verified');
  } catch (err: unknown) {
    errorMessage.value = toErrorMessage(err, t('property.access.otpEntryModal.errorVerifyFailed'));
    code.value = ['', '', '', '', '', ''];
    nextTick(() => inputs.value[0]?.focus());
  }
};

const onInput = (event: Event, idx: number) => {
  errorMessage.value = null;
  const target = event.target as HTMLInputElement;
  const digit = target.value.replace(/\D/g, '').slice(-1);
  code.value[idx] = digit;
  target.value = digit;
  if (digit && idx < 5) inputs.value[idx + 1]?.focus();
  if (idx === 5 && digit) handleSubmit();
};

const onKeydown = (event: KeyboardEvent, idx: number) => {
  if (event.key === 'Backspace') {
    if (code.value[idx]) {
      code.value[idx] = '';
    } else if (idx > 0) {
      inputs.value[idx - 1]?.focus();
      code.value[idx - 1] = '';
    }
  } else if (event.key === 'ArrowLeft' && idx > 0) {
    inputs.value[idx - 1]?.focus();
  } else if (event.key === 'ArrowRight' && idx < 5) {
    inputs.value[idx + 1]?.focus();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    handleSubmit();
  }
};

const onPaste = (event: ClipboardEvent) => {
  const text = event.clipboardData?.getData('text') ?? '';
  const digits = text.replace(/\D/g, '').slice(0, 6).split('');
  if (digits.length === 0) return;
  event.preventDefault();
  for (let i = 0; i < 6; i += 1) {
    code.value[i] = digits[i] ?? '';
  }
  const focusIdx = Math.min(digits.length, 5);
  inputs.value[focusIdx]?.focus();
  if (digits.length === 6) handleSubmit();
};

onMounted(() => {
  handleSend();
});

onBeforeUnmount(() => {
  if (resendInterval) clearInterval(resendInterval);
});
</script>

<template>
  <ModalShell
    accent="green"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="phone"
      accent="green"
    />
    <ProfilePhoneEmailFallback
      v-if="isBlocked"
      @verified="emit('verified')"
    />
    <template v-else>
      <div class="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-success">
        {{ t('property.access.otpEntryModal.enterYourCode') }}
      </div>
      <h2
        :id="titleId"
        class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
      >
        {{ t('property.access.otpEntryModal.heading') }}
      </h2>
      <i18n-t
        keypath="property.access.otpEntryModal.bodyRich"
        tag="p"
        class="mb-5 text-[13.5px] leading-[1.55] text-black-70"
      >
        <template #phone
          ><span
            class="font-semibold text-black-100"
            dir="ltr"
            >{{ userPhoneMasked }}</span
          ></template
        >
      </i18n-t>

      <!-- Digit order must stay left-to-right under RTL — pin the row so it never mirrors. -->
      <div
        class="mb-3 flex justify-center gap-1.5"
        dir="ltr"
        @paste="onPaste"
      >
        <input
          v-for="(_cell, idx) in code"
          :key="idx"
          :ref="(el) => setInputRef(el as Element | null, idx)"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="1"
          :value="code[idx]"
          :aria-label="t('property.access.otpEntryModal.digitAria', { number: idx + 1 })"
          class="h-14 w-11 rounded-lg border-2 border-black-10 bg-white text-center text-[20px] font-bold text-black-100 outline-none transition-colors focus:border-success focus:ring-2 focus:ring-success/20"
          :class="{ '!border-error-100': errorMessage }"
          @input="(event) => onInput(event, idx)"
          @keydown="(event) => onKeydown(event, idx)"
        />
      </div>

      <div
        v-if="errorMessage"
        role="alert"
        class="mb-3 rounded-lg bg-error-100/10 px-3 py-2 text-center text-[12.5px] font-semibold text-error-100"
      >
        {{ errorMessage }}
      </div>

      <div class="flex flex-col gap-2">
        <button
          type="button"
          class="inline-flex h-11 items-center justify-center rounded-[10px] bg-success px-5 text-[14px] font-bold text-white transition-colors hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!isComplete || isVerifying"
          @click="handleSubmit"
        >
          {{
            isVerifying ? t('property.access.otpEntryModal.verifying') : t('property.access.otpEntryModal.verifyCode')
          }}
        </button>
        <button
          type="button"
          class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="resendTimer > 0 || isSending"
          @click="handleSend"
        >
          {{
            resendTimer > 0
              ? t('property.access.otpEntryModal.resendIn', { seconds: resendTimer })
              : t('property.access.otpEntryModal.resendCode')
          }}
        </button>
      </div>
    </template>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      <span dir="ltr">{{ userPhoneMasked }}</span> ·
      <button
        type="button"
        class="font-semibold text-black-80 underline-offset-2 hover:underline"
        @click="emit('change-number')"
      >
        {{ t('property.access.shared.changeNumber') }}
      </button>
    </div>
  </ModalShell>
</template>
